import MlInsightsAPI from 'api/ml-insights.api';
import { BaseHandler } from './base.handler';
import { store } from 'store';
import {
  setEvidenceLoading,
  setMlInsights,
  setUpserting,
  updateInsightStatus,
  updateBulkInsightStatus,
  updateInsightActiveByNotificationId,
  addAirFilterInsights,
  appendInsights,
} from 'store/ml-insights.slice';
import type {
  MLInsightsResponse,
  MLImpactResponse,
  SupportingEvidenceResponse,
  UpsertInsightPayload,
  UpdatedInsightsResponse,
  AirFilterInsightsResponse,
  AirFilterEvidenceResponse,
} from 'types/ml-insights.types';

export default class MlInsightsHandler extends BaseHandler {
  private readonly api: MlInsightsAPI;
  constructor() {
    super();
    this.api = new MlInsightsAPI();
  }

  private getCachedData<T>(key: string): T | null {
    const cachedData = sessionStorage.getItem(key);
    return cachedData ? JSON.parse(cachedData) : null;
  }

  private setCachedData<T>(key: string, data: T): void {
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  private getUnitSystem(): string {
    return store.getState().authReducer.customer?.unitSystem ?? 'metric';
  }

  private generateCacheKey(
    key: string,
    date: string,
    others?: Record<string, unknown>
  ): string {
    const unitSystem = this.getUnitSystem();
    return `${key}-${date}-${JSON.stringify({ ...others, unitSystem })}`;
  }

  private normalizeInsightRow<T extends Record<string, unknown>>(row: T): T {
    const trimmedIssue =
      typeof row.issue === 'string' ? row.issue.trim() : row.issue;
    const trimmedInsight =
      typeof row.insight === 'string' ? row.insight.trim() : row.insight;
    const label = String(trimmedInsight || trimmedIssue || 'Unknown Issue');
    const issueOut =
      trimmedIssue != null && String(trimmedIssue).length > 0
        ? trimmedIssue
        : label;

    const normalized: T = {
      ...row,
      issue: issueOut,
      insight: label,
    };
    return normalized;
  }

  async getMlInsights(
    range: string,
    startDate: string,
    endDate: string,
    options?: {
      page?: number;
      limit?: number;
      isActive?: boolean;
    }
  ): Promise<MLInsightsResponse> {
    try {
      const cacheKey =
        startDate === '' && endDate === ''
          ? this.generateCacheKey(
              'ml-insights',
              new Date().toISOString().split('T')[0],
              {
                range,
                page: options?.page,
                limit: options?.limit,
                isActive: options?.isActive,
              }
            )
          : this.generateCacheKey('ml-insights', startDate, {
              range,
              endDate,
              page: options?.page,
              limit: options?.limit,
              isActive: options?.isActive,
            });
      const cachedData = this.getCachedData<MLInsightsResponse>(cacheKey);

      if (cachedData) {
        const normalizedCachedInsights =
          cachedData.insights?.map(
            (insight: MLInsightsResponse['insights'][0]) =>
              this.normalizeInsightRow({ ...insight })
          ) ?? [];
        this.dispatch(setMlInsights({ summary: cachedData.summary }));
        this.dispatch(appendInsights(normalizedCachedInsights));
        return { ...cachedData, insights: normalizedCachedInsights };
      }

      const response = await this.api.getMlInsightsV2(
        range,
        startDate,
        endDate,
        options
      );

      const transformedInsights =
        response.insights?.map((insight: MLInsightsResponse['insights'][0]) => {
          // Normalize time fields so the UI can rely on `timestamp` and
          // `alertStartTs/alertEndTs` for cards, tables, and evidence charts.
          const startRaw =
            insight.alertStartTs ?? insight.timestamp ?? insight.datetime ?? '';

          const endRaw = insight.alertEndTs ?? '';

          return this.normalizeInsightRow({
            ...insight,
            timestamp: startRaw,
            alertStartTs: insight.alertStartTs ?? startRaw,
            alertEndTs: insight.alertEndTs ?? endRaw,
          });
        }) ?? [];

      this.dispatch(
        setMlInsights({
          summary: response.summary,
        })
      );

      this.dispatch(appendInsights(transformedInsights));

      const transformedResponse: MLInsightsResponse = {
        ...response,
        insights: transformedInsights,
      };

      this.setCachedData(cacheKey, transformedResponse);
      return transformedResponse;
    } catch (error) {
      console.error('Error fetching ML insights:', error);
      throw error;
    }
  }

  async getMLImpact(insight: string): Promise<MLImpactResponse> {
    try {
      // const cacheKey = this.generateCacheKey(
      //   'ml-impact',
      //   new Date().toISOString()
      // );
      const cacheKey = this.generateCacheKey('ml-impact', insight, {});
      const cachedData = this.getCachedData<MLImpactResponse>(cacheKey);

      if (cachedData) {
        return cachedData;
      }

      const response = await this.api.getMLImpact(insight);
      this.setCachedData(cacheKey, response);
      return response;
    } catch (error) {
      console.error('Error fetching ML impact:', error);
      throw error;
    }
  }

  async getSupportingEvidence(
    dateTime: string,
    device: string,
    insight: string,
    isTransmissionInefficiency: boolean,
    isAirFilter = false
  ): Promise<SupportingEvidenceResponse | AirFilterEvidenceResponse> {
    try {
      this.dispatch(setEvidenceLoading(true));
      // Key bumped to -v2 when yhat_bound/bound_type were added to the response:
      // entries cached under the old key lack those fields and would suppress
      // the threshold line until the browser tab was closed.
      const cacheKey = this.generateCacheKey(
        'supporting-evidence-v2',
        dateTime,
        {
          device,
          insight,
        }
      );
      const cachedData = this.getCachedData<
        SupportingEvidenceResponse | AirFilterEvidenceResponse
      >(cacheKey);

      if (cachedData) {
        this.dispatch(setEvidenceLoading(false));
        return cachedData;
      }
      if (isAirFilter) {
        const response = await this.api.getAirFilterEvidence(
          dateTime,
          device,
          insight
        );
        this.setCachedData(cacheKey, response);
        this.dispatch(setEvidenceLoading(false));
        return response;
      }

      const response = await this.api.getSupportingEvidence(
        dateTime,
        device,
        insight,
        isTransmissionInefficiency
      );
      this.setCachedData(cacheKey, response);
      this.dispatch(setEvidenceLoading(false));
      return response;
    } catch (error) {
      console.error('Error fetching supporting evidence:', error);
      throw error;
    }
  }

  /**
   * One evidence-values-v2 request per tag (parallel). Same response shape as v1 evidence.
   * @param fromTs - ISO 8601 UTC window start
   * @param toTs - ISO 8601 UTC window end (optional)
   */
  async getSupportingEvidenceV2ForTags(
    tagNames: string[],
    device: string,
    fromTs: string,
    toTs?: string
  ): Promise<Array<{ tag: string; evidence: SupportingEvidenceResponse }>> {
    this.dispatch(setEvidenceLoading(true));
    try {
      const settled = await Promise.allSettled(
        tagNames.map(async (selectedTag) => {
          const cacheKey = this.generateCacheKey(
            'supporting-evidence-v2',
            fromTs,
            { device, selectedTag, ...(toTs ? { toTs } : {}) }
          );
          const cached =
            this.getCachedData<SupportingEvidenceResponse>(cacheKey);
          if (cached) {
            return { tag: selectedTag, evidence: cached };
          }
          const evidence = await this.api.getSupportingEvidenceV2({
            selectedTag,
            device,
            fromTs,
            toTs,
          });
          this.setCachedData(cacheKey, evidence);
          return { tag: selectedTag, evidence };
        })
      );
      const failedTags: string[] = [];
      const successfulResults: Array<{
        tag: string;
        evidence: SupportingEvidenceResponse;
      }> = [];

      settled.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          successfulResults.push(result.value);
          return;
        }
        failedTags.push(tagNames[index]);
      });

      if (failedTags.length > 0) {
        console.error(
          'Error fetching supporting evidence for selected tags:',
          failedTags
        );
      }

      return successfulResults;
    } finally {
      this.dispatch(setEvidenceLoading(false));
    }
  }

  async upsertInsight(data: UpsertInsightPayload) {
    try {
      this.dispatch(setUpserting(true));
      const response = await this.api.upsertInsight(data);

      this.dispatch(
        updateInsightStatus({
          insightName: data.insightName,
          deviceId: data.deviceId,
          status: data.status,
          insightDate: data.insightDate,
        })
      );

      this.dispatch(setUpserting(false));
      return response;
    } catch (error) {
      console.error('Error upserting insight:', error);
      this.dispatch(setUpserting(false));
      throw error;
    }
  }

  async getUpdatedInsights(): Promise<UpdatedInsightsResponse> {
    const response = await this.api.getUpsertedInsight();

    // Transform insights to ensure they have the correct structure
    const transformedInsights =
      response.insights?.map(
        (insight: UpdatedInsightsResponse['insights'][0]) => ({
          insightName: insight.insightName ?? insight.insight,
          deviceId: insight.deviceId ?? insight.device,
          status: insight.status,
          insightDate:
            insight.insightDate ?? insight.timestamp ?? insight.datetime,
        })
      ) ?? [];

    this.dispatch(updateBulkInsightStatus(transformedInsights));
    return response;
  }

  async getAirFilterInsights(
    range: string,
    startDate: string,
    endDate: string
  ): Promise<AirFilterInsightsResponse> {
    try {
      const cacheKey = this.generateCacheKey('air-filter-insights', startDate, {
        endDate,
      });
      const cachedData =
        this.getCachedData<AirFilterInsightsResponse>(cacheKey);

      if (cachedData) {
        const transformedFormattedInsights = cachedData.formattedInsights.map(
          (insight: AirFilterInsightsResponse['formattedInsights'][0]) => {
            const startRaw =
              insight.alertStartTs ??
              insight.timestamp ??
              insight.datetime ??
              '';

            const endRaw = insight.alertEndTs ?? '';

            return this.normalizeInsightRow({
              ...insight,
              timestamp: startRaw,
              alertStartTs: insight.alertStartTs ?? startRaw,
              alertEndTs: insight.alertEndTs ?? endRaw,
            });
          }
        );

        this.dispatch(appendInsights(transformedFormattedInsights));
        this.dispatch(addAirFilterInsights(cachedData.response));
        return {
          ...cachedData,
          formattedInsights: transformedFormattedInsights,
        };
      }

      const response = await this.api.getAirFilterInsights(
        range,
        startDate,
        endDate
      );

      const transformedFormattedInsights = response.formattedInsights.map(
        (insight: AirFilterInsightsResponse['formattedInsights'][0]) => {
          const startRaw =
            insight.alertStartTs ?? insight.timestamp ?? insight.datetime ?? '';

          const endRaw = insight.alertEndTs ?? '';

          return this.normalizeInsightRow({
            ...insight,
            timestamp: startRaw,
            alertStartTs: insight.alertStartTs ?? startRaw,
            alertEndTs: insight.alertEndTs ?? endRaw,
          });
        }
      );

      this.dispatch(appendInsights(transformedFormattedInsights));

      this.dispatch(addAirFilterInsights(response.response));
      const transformedResponse = {
        ...response,
        formattedInsights: transformedFormattedInsights,
      };
      this.setCachedData(cacheKey, transformedResponse);
      return transformedResponse;
    } catch (error) {
      console.error('Error fetching air filter insights:', error);
      throw error;
    }
  }

  async updateMlInsightStatus(notificationId: string, isActive: boolean) {
    try {
      const response = await this.api.updateMlInsightStatus(
        notificationId,
        isActive
      );

      this.dispatch(
        updateInsightActiveByNotificationId({
          notificationId,
          isActive,
        })
      );

      return response;
    } catch (error) {
      console.error('Error updating ML insight status:', error);
      throw error;
    }
  }
}
